// OperationsPage.tsx — Section III: Operations.
//
// "Cycles, heap, memory. The vital signs of every canister on the network."
// (Lore-approved OP-03)
//
// Wire: get_all_canister_cycles() + get_backend_principals() → per-canister
// status cards. Switched from admin_get_all_canister_health() 2026-05-22 —
// see useCanisterHealth.ts header for the rationale (4-of-7 coverage,
// always-zero heap, IDL `label_`/`label` decode mismatch).
//
// Status thresholds (per copy table OP-04/OP-05):
//   healthy   — cycles > 2T AND (heap unmeasured OR heap < 70%)
//   attention — cycles <= 2T OR heap >= 70%
//   critical  — cycles <= 1T (threshold trigger) OR heap >= 90%
//
// Heap + memory remain unmeasured at the Motoko layer pending dfx 0.30+ /
// moc RTS heap bindings. The cards render em-dashes for those rows rather
// than misleading 0% bars.
//
// P1 (ic-dashboard): ICP Network section below canister cards —
//   avg cycle burn rate + ICP/XDR rate. Mainnet-only.
//
// P4 (canhelp + ic-dashboard): Canister Inspector <details> panel per card —
//   wasm module hash, controllers, status, subnet ID. Mainnet-only.
//   Includes hash verification against expectedCanisterHashes.ts.

import React, { useState } from 'react';
import type { Identity } from '@dfinity/agent';
import type { CanisterRollup } from '../lib/adminAgent';
import { useCanisterHealth } from '../hooks/useCanisterHealth';
import { useAutoTopUp } from '../hooks/useAutoTopUp';
import { useNetworkHealth } from '../hooks/useNetworkHealth';
import { useCanisterInspector } from '../hooks/useCanisterInspector';
import { verifyHash } from '../lib/expectedCanisterHashes';
import type { CanisterInspection } from '../hooks/useCanisterInspector';

interface OperationsPageProps {
  identity: Identity | null;
}

// 1 trillion cycles
const T_CYCLES = 1_000_000_000_000n;

type HealthLevel = 'healthy' | 'attention' | 'critical';

function classifyHealth(r: CanisterRollup): HealthLevel {
  const cycles = r.health.cycles;
  // Heap classification is only meaningful when the source canister actually
  // measures heap. dfx 0.25 / moc 0.13 do not expose RTS heap, so the hook
  // surfaces 0 sentinels — ignore those rather than flagging every canister
  // "attention" for a fake 0% reading.
  const heapMeasured = r.health.heapBytes > 0n || r.health.heapPercent > 0n;
  const heap = Number(r.health.heapPercent);
  if (cycles <= T_CYCLES || (heapMeasured && heap >= 90)) return 'critical';
  if (cycles <= 2n * T_CYCLES || (heapMeasured && heap >= 70)) return 'attention';
  return 'healthy';
}

function formatTCycles(n: bigint): string {
  // Display as "X.XXt" where t = trillion
  const tc = Number(n) / 1e12;
  if (tc === 0) return '0t';
  return tc.toFixed(2).replace(/\.?0+$/, '') + 't';
}

/// Treasury / Total formatter — shows "X.X T" (one decimal, trailing zero
/// trimmed) to read at a glance. Diverges from formatTCycles intentionally:
/// the summary numerals are the most-glanced figures on the page, so we
/// trade precision (2 decimals) for legibility (1 decimal + space + unit).
function formatSummaryCycles(n: bigint | null): string {
  if (n === null) return '—';
  const tc = Number(n) / 1e12;
  if (tc === 0) return '0 T';
  return tc.toFixed(1).replace(/\.0$/, '') + ' T';
}

// ── P4: Inspector details panel ───────────────────────────────────────────────

interface InspectorPanelProps {
  label: string;
  inspection: CanisterInspection | undefined;
  inspectorError: string | undefined;
  isMainnet: boolean;
}

function InspectorPanel({
  label,
  inspection,
  inspectorError,
  isMainnet,
}: InspectorPanelProps): React.ReactElement {
  const [userHash, setUserHash] = useState('');
  // Copy-feedback state for the WASM hash copy button. Without this, the
  // copy click was silent — the operator could not tell whether the
  // clipboard had been written. Matches IcpTopUpPanel.handleCopy pattern.
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>('idle');
  const copyTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  React.useEffect(() => () => {
    if (copyTimerRef.current !== null) clearTimeout(copyTimerRef.current);
  }, []);

  const handleCopyHash = async () => {
    if (!inspection?.moduleHash) return;
    try {
      await navigator.clipboard.writeText(inspection.moduleHash);
      setCopyState('copied');
    } catch {
      setCopyState('error');
    }
    if (copyTimerRef.current !== null) clearTimeout(copyTimerRef.current);
    copyTimerRef.current = setTimeout(() => setCopyState('idle'), 2200);
  };

  if (!isMainnet) {
    return (
      <details className="inspector-details">
        <summary className="inspector-summary">Inspector</summary>
        <p className="inspector-placeholder">
          Inspector available on mainnet only.
        </p>
      </details>
    );
  }

  if (inspectorError) {
    return (
      <details className="inspector-details">
        <summary className="inspector-summary">Inspector</summary>
        <p className="inspector-placeholder">{inspectorError}</p>
      </details>
    );
  }

  if (!inspection) {
    return (
      <details className="inspector-details">
        <summary className="inspector-summary">Inspector</summary>
        <p className="inspector-placeholder">Loading canister data.</p>
      </details>
    );
  }

  // Hash verification against expectedCanisterHashes.ts constants
  const verifyResult = userHash.trim()
    ? (inspection.moduleHash.toLowerCase() === userHash.trim().toLowerCase() ? 'match' : 'mismatch')
    : verifyHash(label, inspection.moduleHash);

  return (
    <details className="inspector-details">
      <summary className="inspector-summary">Inspector</summary>

      <dl className="inspector-dl">
        <div className="inspector-row">
          <dt className="inspector-dt">Status</dt>
          <dd className="inspector-dd inspector-status">{inspection.status}</dd>
        </div>

        <div className="inspector-row">
          <dt className="inspector-dt">WASM hash</dt>
          <dd className="inspector-dd">
            <span className="inspector-hash-short">
              {inspection.moduleHashShort
                ? `…${inspection.moduleHashShort}`
                : '—'}
            </span>
            {inspection.moduleHash && (
              <>
                <button
                  className="inspector-copy-btn"
                  title="Copy full hash"
                  onClick={() => { handleCopyHash().catch(() => undefined); }}
                >
                  {copyState === 'copied' ? 'copied' : copyState === 'error' ? 'copy failed' : 'copy'}
                </button>
              </>
            )}
          </dd>
        </div>

        <div className="inspector-row">
          <dt className="inspector-dt">Controllers</dt>
          <dd className="inspector-dd">{inspection.controllers.length}</dd>
        </div>

        <div className="inspector-row">
          <dt className="inspector-dt">Subnet</dt>
          <dd className="inspector-dd inspector-mono">
            {inspection.subnetId
              ? inspection.subnetId.slice(0, 16) + '…'
              : '—'}
          </dd>
        </div>
      </dl>

      {/* Hash verification */}
      <div className="inspector-verify">
        <label className="inspector-verify-label" htmlFor={`verify-${label}`}>
          Verify hash
        </label>
        <input
          id={`verify-${label}`}
          className="inspector-verify-input"
          type="text"
          placeholder="Paste expected hash from CI artifact"
          value={userHash}
          onChange={(e) => setUserHash(e.target.value)}
        />
        {(userHash.trim() || verifyResult !== 'unknown') && (
          <span
            className={`inspector-verify-result inspector-verify-result--${verifyResult}`}
          >
            {verifyResult === 'match'
              ? 'Hash verified.'
              : verifyResult === 'mismatch'
                ? 'Hash mismatch — upgrade may not have applied.'
                : 'No expected hash recorded.'}
          </span>
        )}
      </div>
    </details>
  );
}

// ── Canister card (with P4 inspector) ────────────────────────────────────────

interface CanisterCardProps {
  rollup: CanisterRollup;
  inspection: CanisterInspection | undefined;
  inspectorError: string | undefined;
  isMainnet: boolean;
}

function CanisterCard({
  rollup,
  inspection,
  inspectorError,
  isMainnet,
}: CanisterCardProps): React.ReactElement {
  const level = classifyHealth(rollup);
  const statusLabel = level === 'critical' ? 'critical' : level === 'attention' ? 'attention' : 'healthy';

  // A label suffix " (status failed)" or " (not wired)" means the canister
  // could not be reached or has no principal registered. In both cases the
  // cycle balance is 0 by canister-side fallback, so we render the offline
  // copy rather than misleading 0t cycles + empty bars.
  const isUnreachable = / \((status failed|not wired)\)$/.test(rollup.label);
  const isDown = isUnreachable || rollup.health.cycles === 0n;

  // Heap / memory are not currently measured at the Motoko layer (dfx 0.25 /
  // moc 0.13 do not expose RTS heap stats). The hook surfaces 0 sentinels;
  // we render em-dashes here so operators do not read "0%" as "healthy".
  // v1.1 follow-up: once RTS bindings land, render real bars again.
  const heapMeasured = rollup.health.heapBytes > 0n || rollup.health.heapPercent > 0n;
  const memMeasured = rollup.health.memoryBytes > 0n || rollup.health.memoryPercent > 0n;
  const heapPct = Number(rollup.health.heapPercent);
  const memPct = Number(rollup.health.memoryPercent);

  return (
    <article className={`card health-card health-card--${level}`}>
      <div className="card-label">
        <span className="card-label-text">{rollup.label}</span>
        <span className={`health-status-badge health-status-badge--${level}`}>{statusLabel}</span>
      </div>

      {isDown ? (
        <p className="card-caption">Canister unreachable. Awaiting first health snapshot. Check connection or cycle balance.</p>
      ) : (
        <>
          <div className="card-numeral">
            {formatTCycles(rollup.health.cycles)}
            <span className="card-numeral-sub"> cycles</span>
          </div>

          <div className="health">
            <div className="health-meta">
              <span>Heap</span>
              <span>{heapMeasured ? `${heapPct}%` : '—'}</span>
            </div>
            {heapMeasured && (
              <div className="health-bar">
                <div
                  className={`health-bar-fill${heapPct >= 70 ? ' is-warn' : ''}`}
                  style={{ width: `${Math.min(heapPct, 100)}%` }}
                />
              </div>
            )}

            <div className="health-meta" style={{ marginTop: 'var(--s-sm)' }}>
              <span>Memory</span>
              <span>{memMeasured ? `${memPct}%` : '—'}</span>
            </div>
            {memMeasured && (
              <div className="health-bar">
                <div
                  className={`health-bar-fill${memPct >= 70 ? ' is-warn' : ''}`}
                  style={{ width: `${Math.min(memPct, 100)}%` }}
                />
              </div>
            )}
          </div>

          <p className="card-caption" style={{ marginTop: 'var(--s-sm)' }}>
            {rollup.principal.toText().slice(0, 20)}…
          </p>
        </>
      )}

      {/* P4: Inspector details panel — visible on mainnet, placeholder locally */}
      <InspectorPanel
        label={rollup.label}
        inspection={inspection}
        inspectorError={inspectorError}
        isMainnet={isMainnet}
      />
    </article>
  );
}

// ── P1: Network health row ────────────────────────────────────────────────────

interface NetworkRowProps {
  isMainnet: boolean;
  isLoading: boolean;
  error: string | null;
  data: {
    avgCycleBurnRateDisplay: string;
    icpPerXdrDisplay: string;
  } | null;
  lastFetchedAt: Date | null;
  refresh: () => void;
}

function NetworkHealthRow({
  isMainnet,
  isLoading,
  error,
  data,
  lastFetchedAt,
  refresh,
}: NetworkRowProps): React.ReactElement {
  return (
    <section className="network-health-section" aria-label="ICP Network">
      <header className="network-health-header">
        <span className="label">ICP Network</span>
        {isMainnet && lastFetchedAt && (
          <span className="section-meta" style={{ marginTop: 0 }}>
            {lastFetchedAt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })} UTC
            {' '}
            <button className="btn-inline" onClick={refresh} disabled={isLoading}>
              {isLoading ? 'Refreshing' : 'Refresh'}
            </button>
          </span>
        )}
      </header>

      {!isMainnet && (
        <p className="network-health-placeholder">
          Network metrics available on mainnet only.
        </p>
      )}

      {isMainnet && error && !data && (
        <p className="network-health-placeholder">{error}</p>
      )}

      {isMainnet && (
        <div className="network-health-cards">
          <article className="card network-card">
            <div className="card-label">
              <span className="card-label-text">Avg Cycle Burn</span>
            </div>
            <div className="card-numeral network-card-numeral">
              {data ? data.avgCycleBurnRateDisplay : isLoading ? '—' : '—'}
            </div>
            <p className="card-caption">across all IC subnets</p>
          </article>

          <article className="card network-card">
            <div className="card-label">
              <span className="card-label-text">ICP / XDR Rate</span>
            </div>
            <div className="card-numeral network-card-numeral">
              {data ? data.icpPerXdrDisplay : isLoading ? '—' : '—'}
              {data && <span className="card-numeral-sub"> XDR</span>}
            </div>
            <p className="card-caption">current conversion rate</p>
          </article>
        </div>
      )}
    </section>
  );
}

// ── Treasury summary ──────────────────────────────────────────────────────────
//
// Aggregate cycle figures rendered above the per-canister breakdown. Four
// load-bearing numerals:
//   1. Treasury available — dominus_admin's own balance, the pool Auto-Top-Up
//      and Manual Top-Up draw from when distributing to the other 6 canisters.
//   2. Total deployed — sum across all 7 canisters; the "how much cycle runway
//      does Dominus have right now" figure.
//   3. Available to distribute — min(treasury, dailyCapRemaining). The
//      practical "how many cycles can I actually move from dominus_admin into
//      the other canisters today, right now" number. The caption surfaces
//      which limiter is binding so the operator knows whether to lift the cap
//      (admin lever) or top up the treasury (Jerry-manual ICP→cycles flow).
//   4. Canister count — fixed at the number of rollup entries returned.
//
// `capRemaining` is sourced from `get_auto_top_up_config` (query — fast, free,
// auto-resets at midnight UTC inside the canister via `capRemainingFromState`).
// No new canister method needed.
//
// Operator decision use-case: at a glance, decide whether the treasury can
// absorb a manual top-up of N trillion to a specific canister without dipping
// below the auto-top-up threshold OR breaching the daily safety cap.

interface TreasurySummaryProps {
  treasuryCycles: bigint | null;
  totalCycles: bigint | null;
  /// Daily cap remaining from get_auto_top_up_config().capRemaining. Null
  /// while the auto-top-up config is loading or unavailable.
  capRemaining: bigint | null;
  canisterCount: number;
  lastFetchedAt: Date | null;
}

/// Compute the practical distribute-now ceiling and a one-line caption that
/// names the binding limiter. Returns null available when either input is null
/// (avoids surfacing a misleading "0 T" before the config has loaded).
function deriveAvailableToDistribute(
  treasury: bigint | null,
  cap: bigint | null,
): { available: bigint | null; caption: string } {
  if (treasury === null || cap === null) {
    return {
      available: null,
      caption: 'Refresh required to compute the live distribute-now ceiling.',
    };
  }
  if (cap === 0n) {
    return {
      available: 0n,
      caption: 'Daily cap reached. The window resets at midnight UTC.',
    };
  }
  if (treasury <= cap) {
    return {
      available: treasury,
      caption: 'Treasury-limited. Top up dominus_admin to lift this ceiling.',
    };
  }
  return {
    available: cap,
    caption: 'Daily cap-limited. Raise the cap in Controls to lift this ceiling.',
  };
}

function TreasurySummary({
  treasuryCycles,
  totalCycles,
  capRemaining,
  canisterCount,
  lastFetchedAt,
}: TreasurySummaryProps): React.ReactElement {
  const { available, caption } = deriveAvailableToDistribute(treasuryCycles, capRemaining);
  return (
    <section className="treasury-summary" aria-label="Treasury status">
      <header className="treasury-summary-header">
        <span className="label">Treasury Status</span>
        {lastFetchedAt && (
          <span className="section-meta" style={{ marginTop: 0 }}>
            Updated · {lastFetchedAt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })} UTC
          </span>
        )}
      </header>

      <div className="grid grid-4 treasury-summary-grid treasury-summary-grid--four">
        <article className="card treasury-card treasury-card--primary">
          <div className="card-label">
            <span className="card-label-text">Treasury available</span>
          </div>
          <div className="card-numeral">
            {formatSummaryCycles(treasuryCycles)}
            <span className="card-numeral-sub"> cycles</span>
          </div>
          <p className="card-caption">
            dominus_admin balance. Pool drawn by Auto-Top-Up and Manual Top-Up.
          </p>
        </article>

        <article className="card treasury-card">
          <div className="card-label">
            <span className="card-label-text">Available to distribute</span>
          </div>
          <div className="card-numeral">
            {formatSummaryCycles(available)}
            <span className="card-numeral-sub"> cycles</span>
          </div>
          <p className="card-caption">{caption}</p>
        </article>

        <article className="card treasury-card">
          <div className="card-label">
            <span className="card-label-text">Total deployed</span>
          </div>
          <div className="card-numeral">
            {formatSummaryCycles(totalCycles)}
            <span className="card-numeral-sub"> cycles</span>
          </div>
          <p className="card-caption">
            Sum across all canisters incl. treasury. The runway figure.
          </p>
        </article>

        <article className="card treasury-card">
          <div className="card-label">
            <span className="card-label-text">Cycles per canister</span>
          </div>
          <div className="card-numeral">
            {canisterCount > 0 ? canisterCount : '—'}
            <span className="card-numeral-sub"> monitored</span>
          </div>
          <p className="card-caption">
            See breakdown below for the live balance on each canister.
          </p>
        </article>
      </div>
    </section>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function OperationsPage({ identity }: OperationsPageProps): React.ReactElement {
  const {
    rollups,
    treasuryCycles,
    totalCycles,
    isLoading,
    error,
    lastFetchedAt,
    refresh,
  } = useCanisterHealth(identity);

  // Auto-top-up config — sourced via get_auto_top_up_config (query, polled 60s
  // inside the hook). We read .capRemaining for the "Available to distribute"
  // ceiling on the Treasury Status card. No new canister surface needed.
  const { config: autoTopUpConfig } = useAutoTopUp(identity);
  const capRemaining: bigint | null = autoTopUpConfig?.capRemaining ?? null;

  // P1: IC network-level health (mainnet-only)
  const {
    data: networkData,
    isMainnet,
    isLoading: networkLoading,
    error: networkError,
    lastFetchedAt: networkFetchedAt,
    refresh: networkRefresh,
  } = useNetworkHealth();

  // P4: Per-canister WASM inspection (mainnet-only, triggered after rollups load)
  const {
    inspections,
    errors: inspectionErrors,
  } = useCanisterInspector(rollups);

  return (
    <section className="section" id="operations" aria-labelledby="operations-title">
      <header className="section-header">
        <span className="section-eyebrow">Section III</span>
        <h2 className="section-title" id="operations-title">Operations</h2>
        <p className="section-lede">
          Cycles, heap, memory. The vital signs of every canister on the network.
        </p>
        {lastFetchedAt && (
          <p className="section-meta">
            Last refreshed · {lastFetchedAt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })} UTC
            {' '}
            <button className="btn-inline" onClick={refresh} disabled={isLoading}>
              {isLoading ? 'Refreshing' : 'Refresh'}
            </button>
          </p>
        )}
      </header>

      {error && (
        <div className="inline-error">{error}</div>
      )}

      {/* Treasury summary — load-bearing aggregate numerals above the breakdown.
          Renders even when rollups is empty (numerals fall back to em-dashes). */}
      <TreasurySummary
        treasuryCycles={treasuryCycles}
        totalCycles={totalCycles}
        capRemaining={capRemaining}
        canisterCount={rollups.length}
        lastFetchedAt={lastFetchedAt}
      />

      {rollups.length === 0 && !isLoading && !error ? (
        <div className="empty-state">
          <p className="empty-state-text">
            Awaiting first canister health snapshot. Top up or check connection.
          </p>
        </div>
      ) : (
        <div className="grid grid-4">
          {rollups.map((r) => (
            <CanisterCard
              key={r.label}
              rollup={r}
              inspection={inspections[r.label]}
              inspectorError={inspectionErrors[r.label]}
              isMainnet={isMainnet}
            />
          ))}
        </div>
      )}

      {/* P1: ICP Network health row */}
      <NetworkHealthRow
        isMainnet={isMainnet}
        isLoading={networkLoading}
        error={networkError}
        data={networkData}
        lastFetchedAt={networkFetchedAt}
        refresh={networkRefresh}
      />
    </section>
  );
}
